import {
  Fragment,
  type ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ChatMessage as ChatMessageData } from '@wundo/shared/chat.ts';
import { formatFileSize } from '@wundo/shared/fileSize.ts';
import {
  ChatActivity,
  ChatActivityPanel,
  type ChatActivityStep,
  ChatAttachments,
  ChatCard,
  ChatDrawerBody,
  ChatLink,
  ChatMarkdown,
  ChatMessage,
  ChatMessageList,
  ChatMultiSelect,
  ChatSuggestion,
  ChatSuggestions,
  ChatTyping,
  useFollowBottom,
} from '@wundo/ui';
import { m } from '@/paraglide/messages.js';
import { StreamingReply } from '@/components/StreamingReply.tsx';
import { chatActivityItemLabels } from '@/lib/chatActivityItemLabels.ts';
import { chatMessageActivity } from '@/lib/chatMessageActivity.ts';

/** Who each side of a conversation is called, where the names are shown. */
export interface ChatAuthorLabels {
  user: string;
  wundo: string;
}

export interface ChatBodyProps {
  /** Full screen, where the conversation keeps a column of readable width. */
  expanded?: boolean;
  messages: ChatMessageData[];
  /**
   * What is being written, in the order it is being written. Each sits where it
   * will stand once it is finished, and reads the same — a reply half written
   * is still a reply, not a different kind of thing.
   */
  streaming: readonly { id: string; text: string }[];
  typing: boolean;
  /** The steps Wundo is taking for what it says next, as they happen. */
  activitySteps?: readonly ChatActivityStep[];
  /** The steps Wundo took before each reply, by message id. */
  activityPinned?: ReadonlyMap<string, readonly ChatActivityStep[]>;
  /** What went wrong, if anything, in words the user can act on. */
  error: string | null;
  /** Sends a message on the user's behalf, from a card or a set of options. */
  onSuggestion: (text: string) => void;
  /**
   * Whether a message is already on its way. While it is, nothing in the
   * conversation speaks for the user either, the way the composer is held:
   * one click, one turn.
   */
  sending?: boolean;
  /** Whether the conversation goes back further than what is shown. */
  hasOlder: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => void;
  /**
   * Who is speaking, where the two sides are named. Left out in the drawer,
   * which is always the user and Wundo; a practice conversation is with
   * somebody else, and says so.
   */
  authorLabels?: ChatAuthorLabels;
  /** What the dots say. Left out for Wundo's own. */
  typingLabel?: string;
  /**
   * What stands in for a conversation that has not started. The drawer shows
   * nothing — what there is to ask sits in the pills under it — but a
   * conversation somebody else opens says that it is about to.
   */
  empty?: ReactNode;
  /**
   * Follows a link in the conversation that points at one of our own pages.
   * Left out and the browser follows it itself, reloading the app around the
   * drawer — so every routed page passes it.
   */
  onNavigate?: (path: string) => void;
}

/**
 * How close to the top counts as reaching it. A page is asked for before the
 * reader arrives, so the conversation grows upward under them rather than
 * stopping dead while it loads.
 */
const NEAR_TOP_PX = 120;

/**
 * Everything between the drawer's header and its composer: the conversation so
 * far, and nothing else.
 *
 * Before there is one it is empty — genuinely empty, not a greeting bubble
 * standing in for a turn nobody has taken. What there is to ask about here is
 * offered by the pills under it, which is also where it stays once the
 * conversation has started.
 */
export function ChatBody({
  expanded = false,
  messages,
  streaming,
  typing,
  activitySteps = [],
  activityPinned,
  error,
  onSuggestion,
  sending = false,
  hasOlder,
  loadingOlder,
  onLoadOlder,
  authorLabels,
  typingLabel,
  empty,
  onNavigate,
}: ChatBodyProps) {
  const scroller = useRef<HTMLDivElement>(null);
  /*
   * The item of a step read in full, over the conversation. Held here so it
   * goes with the conversation: another one is another ChatBody.
   */
  const [opened, setOpened] = useState<{ stepId: string; index: number } | null>(null);
  const openedStep = useMemo(() => {
    if (!opened) {
      return undefined;
    }

    const pinned = [...(activityPinned?.values() ?? [])].flat();
    const kept = messages.flatMap((message) => chatMessageActivity(message, undefined));
    return [...activitySteps, ...pinned, ...kept].find(
      (step) => step.id === opened.stepId,
    );
  }, [opened, activitySteps, activityPinned, messages]);
  const openItem = (step: ChatActivityStep, index: number) =>
    setOpened({ stepId: step.id, index });
  /* The height the conversation had just before a page was put on top of it. */
  const heightBeforeOlder = useRef<number | null>(null);
  const newest = messages.at(-1)?.id;
  const oldest = messages[0]?.id;
  const streamedLength = streaming.reduce((total, reply) => total + reply.text.length, 0);
  const stepRunning = activitySteps.some((step) => step.state === 'running');
  const activityKey = activitySteps.map((step) => `${step.id}:${step.state}`).join(',');

  /*
   * A reply being written grows without the conversation gaining a message,
   * so how much of it there is says there is more to follow down to.
   */
  const { followGrowth } = useFollowBottom(
    scroller,
    `${newest}|${typing}|${activityKey}|${messages.length}|${streamedLength}`,
    messages.length > 0,
  );

  /*
   * An older page lands above whatever is on screen, which would otherwise
   * push it down by exactly the height of what was added. Putting that height
   * back leaves the reader looking at the same words they were.
   */
  useLayoutEffect(() => {
    const element = scroller.current;
    const before = heightBeforeOlder.current;

    if (!element || before === null) {
      return;
    }

    element.scrollTop += element.scrollHeight - before;
    heightBeforeOlder.current = null;
  }, [oldest]);

  function onScroll() {
    const element = scroller.current;

    if (!element) {
      return;
    }

    if (!hasOlder || loadingOlder || element.scrollTop > NEAR_TOP_PX) {
      return;
    }

    heightBeforeOlder.current = element.scrollHeight;
    onLoadOlder();
  }

  return (
    <>
      <ChatDrawerBody
        ref={scroller}
        onScroll={onScroll}
        expanded={expanded}
        onNavigate={onNavigate}
      >
        {loadingOlder ? (
          <p className='pb-3 text-center text-xs text-ink-muted'>
            {m.chat_loading_older()}
          </p>
        ) : null}

        {empty !== undefined && messages.length === 0 && streaming.length === 0
          ? empty
          : null}

        <ChatMessageList>
          {/*
            One array for both, so a finished reply keeps the key it streamed
            under and its bubble stays mounted instead of being swapped out.
          */}
          {[
            ...messages.map((message, index) => (
              <Fragment key={message.id}>
                <ChatActivity
                  steps={chatMessageActivity(message, activityPinned)}
                  failedLabel={m.chat_activity_failed()}
                  detailsLabel={m.chat_activity_details()}
                  onOpenItem={openItem}
                />
                <ChatMessage
                  author={message.author}
                  authorLabel={
                    message.author === 'user'
                      ? (authorLabels?.user ?? m.chat_author_user())
                      : (authorLabels?.wundo ?? m.chat_author_wundo())
                  }
                >
                  <MessageBody
                    message={message}
                    onAction={onSuggestion}
                    /*
                     * Only the last thing said is still open. Anything above it has
                     * been answered — by the reply that followed it — and while a
                     * message is on its way the last one has been answered too.
                     */
                    answered={
                      sending || streaming.length > 0 || index < messages.length - 1
                    }
                  />
                </ChatMessage>
              </Fragment>
            )),
            ...streaming.map((reply) => (
              <Fragment key={reply.id}>
                <ChatActivity
                  steps={activityPinned?.get(reply.id) ?? []}
                  failedLabel={m.chat_activity_failed()}
                  detailsLabel={m.chat_activity_details()}
                  onOpenItem={openItem}
                />
                <ChatMessage
                  author='wundo'
                  authorLabel={authorLabels?.wundo ?? m.chat_author_wundo()}
                >
                  <StreamingReply text={reply.text} onGrow={followGrowth} />
                </ChatMessage>
              </Fragment>
            )),
          ]}

          <ChatActivity
            steps={activitySteps}
            failedLabel={m.chat_activity_failed()}
            detailsLabel={m.chat_activity_details()}
            onOpenItem={openItem}
          />
        </ChatMessageList>

        {/* A running step already says Wundo is busy; the dots fill the gaps between steps. */}
        {typing && !stepRunning ? (
          <ChatTyping label={typingLabel ?? m.chat_typing()} className='mt-3.5' />
        ) : null}

        {error ? (
          <p role='alert' className='mt-3.5 text-sm text-danger'>
            {error}
          </p>
        ) : null}
      </ChatDrawerBody>

      {opened && openedStep ? (
        <ChatActivityPanel
          step={openedStep}
          index={opened.index}
          onIndexChange={(index) => setOpened({ ...opened, index })}
          onClose={() => setOpened(null)}
          kind={chatActivityItemLabels(openedStep.icon).kind}
          openLabel={chatActivityItemLabels(openedStep.icon).open}
          closeLabel={m.chat_activity_panel_close()}
          previousLabel={m.chat_activity_panel_previous()}
          nextLabel={m.chat_activity_panel_next()}
          positionLabel={(position, count) =>
            m.chat_activity_panel_position({ position, count })
          }
          onNavigate={onNavigate}
        />
      ) : null}
    </>
  );
}

/**
 * One message's contents. A card or a set of options is still a message; only
 * the way it is laid out differs, and picking an option says its title — or,
 * where the question takes several, holds it until the answer is confirmed.
 *
 * An answered message keeps everything it showed, so the conversation reads
 * back whole — but what it once offered is greyed rather than clickable.
 */
function MessageBody({
  message,
  onAction,
  answered,
}: {
  message: ChatMessageData;
  onAction: (text: string) => void;
  answered: boolean;
}) {
  const body = message.body;

  switch (body.type) {
    case 'text':
      return <ChatMarkdown>{body.text}</ChatMarkdown>;

    /*
     * A file the user shared, sitting in their own bubble — so the chips are
     * drawn for that background rather than for a plain surface.
     */
    case 'attachment':
      return (
        <>
          <ChatAttachments
            tone={message.author === 'user' ? 'onPrimary' : 'surface'}
            files={body.files.map((file) => ({
              kind: file.kind,
              name: file.name,
              detail: formatFileSize(file.size),
              href: file.url,
            }))}
          />
          {body.text ? <ChatMarkdown className='mt-2'>{body.text}</ChatMarkdown> : null}
        </>
      );

    case 'link':
      return <ChatLink text={body.text} linkText={body.link_text} url={body.url} />;

    case 'card':
      return (
        <ChatCard
          header={body.header}
          body={body.body}
          footer={body.footer}
          sections={body.sections}
          buttons={body.buttons}
          onAction={onAction}
          disabled={answered}
        />
      );

    case 'template':
      return (
        <>
          <ChatMarkdown>{body.text}</ChatMarkdown>
          {body.allow_multiple ? (
            <ChatMultiSelect
              label={m.chat_suggestions_label()}
              options={body.options}
              confirmLabel={m.chat_multi_select_confirm()}
              /*
               * The answer is one message naming everything picked, joined the
               * way Teams joins its toggles — the only other channel that can
               * show a multi-select at all. What Wundo reads back must not
               * depend on which channel the user happened to answer from.
               */
              onConfirm={(titles) => onAction(titles.join(', '))}
              disabled={answered}
              className='mt-3'
            />
          ) : (
            <ChatSuggestions label={m.chat_suggestions_label()} className='mt-3'>
              {body.options.map((option) => (
                <ChatSuggestion
                  key={option.id}
                  text={option.title}
                  disabled={answered}
                  onClick={() => onAction(option.title)}
                />
              ))}
            </ChatSuggestions>
          )}
        </>
      );
  }
}
